import {get_full_url, get_origin, get_pathname} from "@core/helpers/url";
import {
	build_product_json_ld,
	json_ld_absolute_url,
	json_ld_media_url,
	json_ld_page_path
} from "@core/helpers/server/product-json-ld";
import {
	get_all_web_settings,
	get_logo_site_server,
	get_storage_prefix_url_server,
	get_route_names_server
} from "@core/helpers/server/settings"


export const get_breadcrumbs_JsonLd_server = async (breadcrumbs) => {
	const origin = await get_origin();
	return ({
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		"itemListElement": breadcrumbs?.map((item, index) => {
			if (index === breadcrumbs.length - 1) {
				return (
					{
						"@type": "ListItem",
						"position": index + 1,
						"name": item?.title,
					}
				)
			} else {
				return (
					{
						"@type": "ListItem",
						"position": index + 1,
						"name": item?.title,
						"item": item?.href ? `${origin}${item.href}` : undefined
					}
				)
			}
		})
	})
};

// `ProductGroup` when the product has variants (`hasVariant` / `variesBy` are not `Product` properties), `url` with one
// `/` (from `seo.full_url`, else the requested path; a bare slug is not a page), the colour label read from `color` or
// `colors`, prices in the site currency like the `product:*` meta tags. See `@core/helpers/server/product-json-ld`.
export const get_product_JsonLd_server = async (product = {}) => {
	const [{web_site_storage_prefix_url, web_site_currency}, origin, request_path] = await Promise.all([
		get_all_web_settings(),
		get_origin(),
		get_pathname(),
	]);
	return build_product_json_ld({
		product,
		origin,
		storage_prefix_url: web_site_storage_prefix_url,
		web_currency: web_site_currency,
		request_path,
	});
};

// A product of a listing: its page url and first image, never `origin/undefined` or `prefix/undefined`.
const json_ld_list_item = (product, index, origin, storage_prefix_url) => ({
	"@type": "ListItem",
	"position": index + 1,
	"url": json_ld_absolute_url(origin, json_ld_page_path(product?.seo)),
	"name": product?.title,
	"image": json_ld_media_url(storage_prefix_url, product?.media_gallery?.[0]?.url)
});

export const get_category_JsonLd_server = async (category, products) => {
	const {web_site_storage_prefix_url} = await get_all_web_settings();
	// `get_origin` is async: without the await every url was `[object Promise]/...`
	const [origin, request_path] = await Promise.all([get_origin(), get_pathname()]);

	return {
		"@context": "https://schema.org",
		"@type": "CollectionPage",
		"name": category?.title,
		"description": category?.description,
		"url": json_ld_absolute_url(origin, json_ld_page_path(category?.seo, request_path)),
		"mainEntity": {
			"@type": "ItemList",
			"itemListElement": (products || [])?.map((product, index) =>
				json_ld_list_item(product, index, origin, web_site_storage_prefix_url))
		}
	};
};

export const get_brand_JsonLd_server = async (brand, products) => {
	const {web_site_storage_prefix_url} = await get_all_web_settings();
	const [origin, request_path] = await Promise.all([get_origin(), get_pathname()]);

	return {
		"@context": "https://schema.org",
		"@type": "CollectionPage",
		"name": `محصولات ${brand?.title}`,
		"url": json_ld_absolute_url(origin, json_ld_page_path(brand?.seo, request_path)),
		"mainEntity": {
			"@type": "Brand",
			"name": brand?.title,
			"logo": typeof brand?.logo === "string"
				? json_ld_media_url(web_site_storage_prefix_url, brand.logo)
				: json_ld_media_url(web_site_storage_prefix_url, brand?.logo?.url)
		},
		"hasPart": {
			"@type": "ItemList",
			"itemListElement": (products || [])?.map((product, index) =>
				json_ld_list_item(product, index, origin, web_site_storage_prefix_url))
		}
	};
};

export const get_search_JsonLd_server = async (query, products) => {
	const {web_site_storage_prefix_url} = await get_all_web_settings();
	const origin = await get_origin();

	return {
		"@context": "https://schema.org",
		"@type": "SearchResultsPage",
		"name": `نتایج جستجو برای ${query}`,
		"url": `${origin}/search${query ? `?q=${encodeURIComponent(query)}` : ""}`,
		"mainEntity": {
			"@type": "ItemList",
			"itemListElement": (products || [])?.map((product, index) =>
				json_ld_list_item(product, index, origin, web_site_storage_prefix_url))
		}
	};
};

// The site's logo setting is a media object (`{ url }`), sometimes stored as a JSON string or a plain path.
const logo_url_of = (logo) => {
	let value = logo;
	if (typeof value === "string" && value.trim().startsWith("{")) {
		try {
			value = JSON.parse(value);
		} catch (e) {
			value = "";
		}
	}
	return typeof value === "string" ? value.trim() : typeof value?.url === "string" ? value.url : "";
};

// BCP 47 tag for `inLanguage`; Persian keeps the tag it always had
const LANGUAGE_TAGS = { fa: "fa-IR" };

/**
 * `BlogPosting` of an article page, built by core's `build_blog_article_json_ld` (`@core/helpers/blog-json-ld`, moved
 * from chawk-bazar / ciseco): `@id`, real image size, `dateModified` never before `datePublished`, an author `Person`
 * with a url only when the author has a page (the site as `Organization` without an author), publisher logo only when
 * the site has one, `keywords` from `seo.keywords`, the page's language, and no empty field (it used to give
 * `inLanguage: "fa-IR"` on every language, `https://site` + `undefined` urls and `.../undefined` images).
 *
 * `data` is the raw article (`getBlogBySlug`) plus `site_name`. `options`: `page_path` (default: the requested path,
 * i.e. the canonical), `blog_home_path` / `blog_title` (`isPartOf` the blog), `word_count`, `reading_minutes`.
 * Without an article title it returns `{}`.
 */
export const get_blog_page_JsonLd_server = async (data, options = {}) => {
	const { build_blog_article_json_ld, json_ld_blog_article, json_ld_path } = require("@core/helpers/blog-json-ld");
	const { get_current_locate_from_cookie } = require("@core/helpers/locate");
	const { get_pathname } = require("@core/helpers/url");

	const [storage_prefix_url, logo_site, origin, locale, pathname] = await Promise.all([
		get_storage_prefix_url_server(),
		get_logo_site_server(),
		get_origin(),
		get_current_locate_from_cookie(),
		get_pathname(),
	]);

	const logo_path = logo_url_of(logo_site);
	const logo_url = !logo_path
		? undefined
		: /^https?:\/\//i.test(logo_path)
			? logo_path
			: `${String(storage_prefix_url || "").replace(/\/+$/, "")}/${logo_path.replace(/^\/+/, "")}`;

	const request_path = typeof pathname === "string" && pathname.startsWith("/") ? pathname : null;
	const page_path = options?.page_path || request_path || json_ld_path(data?.seo?.full_url) || "/";

	const json_ld = build_blog_article_json_ld({
		origin,
		page_path,
		blog_home_path: options?.blog_home_path,
		blog_title: options?.blog_title,
		site_name: data?.site_name,
		logo_url,
		article: json_ld_blog_article(data),
		in_language: LANGUAGE_TAGS[locale] || locale || "fa-IR",
		storage_prefix_url,
		word_count: options?.word_count,
		reading_minutes: options?.reading_minutes,
	});
	return json_ld || {};
};

export const get_corporation_JsonLd_server = async (data) => {
	const get_full_url_server = await get_full_url();
	return ({
		"@context": "https://schema.org",
		"@type": "Corporation",
		"@id": `${get_full_url_server}#corporation`,
		"name": data?.name,
		"legalName": data?.legalName,
		"url": get_full_url_server,
		"logo": data?.logo,
		"foundingDate": data?.foundingDate,
		"founders": data?.founders?.map(item => ({
			"@type": "Person",
			"name": item?.name
		})),
		"inLanguage": data?.inLanguage,
		"foundingLocation": {
			"@type": "Place",
			"address": {
				"@type": "PostalAddress",
				"addressCountry": "IR",
				"addressLocality": data?.foundingLocate
			}
		},
		"address": {
			"@type": "PostalAddress",
			"streetAddress": data?.address,
			"addressLocality": data?.locate,
			"postalCode": data?.postal_code,
			"addressCountry": "IR"
		},
		"email": data?.email,
		"telephone": data?.telephone,
		"contactPoint": data?.contacts?.map(contact => ({
			"@type": "ContactPoint",
			"telephone": contact?.telephone,
			"contactType": contact?.contactType,
			"availableLanguage": contact?.availableLanguage
		})),
		"description": data?.description,
		"numberOfEmployees": data?.numberOfEmployees,
		"sameAs": data?.links
	})
};

export const get_about_page_JsonLd_server = async (data) => {
	const get_full_url_server = await get_full_url();
	const origin = await get_origin();

	return ({
		"@context": "https://schema.org",
		"@type": "AboutPage",
		"url": get_full_url_server,
		"name": data?.name,
		"description": data?.description,
		"inLanguage": data?.inLanguage,
		"isPartOf": {
			"@type": "WebSite",
			"name": data?.name,
			"url": origin
		},
		"mainEntity": {
			"@id": `${get_full_url_server}#corporation`,
		}
	})
};

export const get_contact_page_JsonLd_server = async (data) => {
	const get_full_url_server = await get_full_url();
	const origin = await get_origin();
	const web_route_names = await get_route_names_server();
	return ({
		"@context": "https://schema.org",
		"@type": "ContactPage",
		"name": data?.name,
		"description": data?.description,
		"url": get_full_url_server,
		"inLanguage": data?.inLanguage,
		"isPartOf": {
			"@type": "WebSite",
			"name": data?.name,
			"url": origin
		},
		"mainEntity": {
			"@type": "Organization",
			"name": data?.name,
			"url": `${origin}${web_route_names?.["home-page"] || "/"}`,
			"logo": data?.logo,
			"contactPoint": {
				"@type": "ContactPoint",
				"telephone": data?.telephone,
				"contactType": "customer service",
				"areaServed": "IR",
				"availableLanguage": data?.availableLanguage
			}
		}
	})
};

export const get_privacy_page_JsonLd_server = async (data) => {
	const get_full_url_server = await get_full_url();
	const origin = await get_origin();
	const web_route_names = await get_route_names_server();
	return ({
		"@context": "https://schema.org",
		"@type": "WebPage",
		"name": data?.name,
		"description": data?.description,
		"url": get_full_url_server,
		"mainEntity": {
			"@id": `${origin}${web_route_names?.["home-page"] || "/"}`,
		}
	})
};